import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from 'hooks';
import { SkeletonUi } from 'components/partials';
import { isAppleIOS } from 'utils';

import { ReactComponent as IconCancel } from 'assets/svgs/icn-x-circle-fill.svg';

const LoaderContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 1rem;
`;

export const PdfLoader = () => (
  <LoaderContainer>
    {[...new Array(30)].map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <SkeletonUi key={`pdf-loader-${index}`} shape="radiusHorizone" width={(index % 6 === 0 ? '33%' : '100%')} height="5px" />
    ))}
  </LoaderContainer>
);

const Container = styled.div<{isAppleIOS: boolean}>`
  width: 100%;
  overflow-x: hidden;
  ${(props) => props.isAppleIOS ? 'overflow-y: hidden' : ''};
  position: relative;
  color: #333;

  canvas {
    width: 100% !important;
    height: auto !important;
  }
`;

const Button = styled.button`
  background-color: transparent;
  border: 0;
`;

const CancelButton = styled(Button)<{background: string}>`
  position: absolute;
  z-index: 2;
  right: 0.8rem;
  top: 0.3125rem;
  cursor: pointer;
  position: fixed;
  width: 1.5rem;

  svg {
    fill: ${(props) => props.background};
  }
`;

interface PdfViewerProps extends React.ComponentPropsWithoutRef<'div'> {
  documentFile: any;
  errorMsg: string;
  onCancelClick: Function;
}

export const PdfViewer: React.FC<PdfViewerProps> = ({ documentFile, errorMsg, onCancelClick }) => {
  const { styles } = useTheme();
  const [pdfContent, setPdfContent] = useState<(React.ReactNode | null)[]>([]);

  // Load pdf document
  useEffect(() => {
    const loadPdf = async () => {
      if (!window.pdfjsLib) {
        setPdfContent([errorMsg]);
        return;
      }

      try {
        const loadingTask = window.pdfjsLib.getDocument(documentFile);
        const pdf = await loadingTask.promise;

        const renderPage = async (pageNum: number) => {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.5 });

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) return null;

          canvas.height = viewport.height;
          canvas.width = viewport.width;

          await page.render({
            canvasContext: context,
            viewport,
          }).promise;

          return (
            <div key={pageNum} style={{ marginBottom: '1rem' }}>
              <canvas
                width={canvas.width}
                height={canvas.height}
                ref={(node) => {
                  if (node) {
                    const ctx = node.getContext('2d');
                    if (ctx) {
                      ctx.drawImage(canvas, 0, 0);
                    }
                  }
                }}
              />
            </div>
          );
        };

        const pagePromises = Array.from({ length: pdf.numPages }, (_, i) => renderPage(i + 1));
        const pages = await Promise.all(pagePromises);
        setPdfContent(pages);
      } catch (err: any) {
        console.error(err);
        setPdfContent([errorMsg]);
      }
    };

    if (documentFile) {
      loadPdf();
    }
  }, [documentFile, errorMsg]);

  return (
    <Container isAppleIOS={isAppleIOS()}>
      <CancelButton
        onClick={() => { onCancelClick(); }}
        background={styles.colorPrimaryButtonBackground}
      >
        <IconCancel />
      </CancelButton>
      {pdfContent}
    </Container>
  );
};
